import emailjs from "emailjs-com";

/**
 * The order email. One send per order, to both parties.
 *
 * There used to be two: an alert to NT Sticks on the old shared contact
 * template, and a receipt to the customer. Once the receipt grew to carry the
 * delivery address, phone, payment method and totals it held every field the
 * alert did, so the alert was pure duplication — and EmailJS bills per request
 * rather than per recipient, so two sends halved the monthly order ceiling for
 * nothing. NT Sticks is now BCC'd on the customer's own receipt instead.
 *
 * WHY THE RECIPIENT IS COMPUTED
 * Email is optional at checkout — that is what dropping the Google login gate
 * bought us — so an order can arrive with no customer address. Sending "to the
 * customer" would then send to nobody and the order would reach no inbox at
 * all. In that case the internal address becomes the To, so the same template
 * still delivers the same information and no order goes unannounced.
 *
 * This is a client-side send, which carries two known limits:
 *   1. The keys below ship in the JS bundle, so a determined person can fire
 *      fake order emails at us. Treat the email as a heads-up, not as the
 *      record — Firestore `orders` is the source of truth, and every email
 *      carries the order ID so it can be verified there.
 *   2. If the send fails (offline, EmailJS down, monthly quota hit) the order
 *      is still placed but no email arrives. Failures are logged to Analytics
 *      so they can be spotted after the fact.
 *
 * Same service and public key as the contact form — see Wholesale.js, which
 * still uses the older shared template directly for wholesale leads.
 */
const SERVICE_ID = "service_cott3tr";
const PUBLIC_KEY = "a2RAs1ZB10E12-kyC";

/**
 * The order template — mirrored in emails/order-confirmation.html, which is the
 * reviewable copy; the live copy is in the EmailJS dashboard.
 *
 * Its To Email field reads {{email}} and its Bcc field should read
 * {{bcc_email}} so both recipients are controlled from here. A static address
 * left in the dashboard's Bcc field also works.
 */
const ORDER_TEMPLATE_ID = "template_wh4v5ia";

/** Where NT Sticks reads its orders. BCC'd on every order email. */
const INTERNAL_INBOX = "ntincensesticks@gmail.com";

/**
 * False only if the template id above is ever blanked out. The confirmation
 * screen reads this to decide whether it may promise the customer an email, so
 * it must keep meaning "an order email will actually be sent".
 */
export const CUSTOMER_RECEIPT_ENABLED = Boolean(ORDER_TEMPLATE_ID);

/** The itemised order, shared by both emails so they can never disagree. */
function buildSummary(order) {
  const lines = [];

  order.items.forEach((item, index) => {
    lines.push(
      `${index + 1}. ${item.name} — qty ${item.quantity} — ₹${item.lineTotal}`
    );
  });

  lines.push("");
  lines.push(`Delivery: ₹${order.deliveryCharge}`);
  if (order.codCharge) lines.push(`COD charge: ₹${order.codCharge}`);
  lines.push(`Total: ₹${order.total}`);

  return lines.join("\n");
}

const SITE = "https://www.ntsticks.com";

function absoluteImage(src) {
  if (!src) return "";
  if (/^https?:\/\//i.test(src)) return src;
  return `${SITE}/${String(src).replace(/^(\.\.\/)+/, "").replace(/^\//, "")}`;
}

/**
 * Send the order email.
 *
 * Goes to the customer with NT Sticks BCC'd, or straight to NT Sticks when the
 * customer gave no address. Either way it is one EmailJS request carrying the
 * order id, the items, the totals, the delivery address, the payment
 * instruction and the unboxing requirement — everything the old separate staff
 * alert used to duplicate.
 *
 * Shaped for the template in emails/order-confirmation.html, which iterates
 * {{#orders}} and reads a nested {{cost.*}} object.
 *
 * Resolves false instead of throwing — an order must never fail because its
 * email didn't send.
 */
export default async function sendOrderEmail(order) {
  if (!CUSTOMER_RECEIPT_ENABLED) return false;

  // The customer when we have them, the internal inbox when we don't — so the
  // order always lands somewhere a human reads.
  const recipient = order.email || INTERNAL_INBOX;
  // Skip the BCC when the internal inbox is already the To, so the same mailbox
  // isn't sent the same message twice.
  const bcc = recipient === INTERNAL_INBOX ? "" : INTERNAL_INBOX;

  const paymentNote =
    order.paymentMethod === "Cash on Delivery"
      ? `Pay ₹${order.total} in cash when the parcel arrives.`
      : `Please send ₹${order.total} by UPI to complete this order, and quote ${order.orderId} in the payment note so we can match it.`;

  try {
    await emailjs.send(
      SERVICE_ID,
      ORDER_TEMPLATE_ID,
      {
        // Recipient under both names, since the stock template's To Email
        // field ships as {{email}} while a hand-made one usually uses
        // {{to_email}}.
        to_email: recipient,
        email: recipient,
        bcc_email: bcc,

        order_id: order.orderId,
        customer_name: order.customerName,

        // {{#orders}} loop. Keys are the stock template's: name, units, price,
        // image.
        orders: order.items.map((item) => ({
          name: item.name,
          units: item.quantity,
          price: item.lineTotal,
          // The stock template's <img src> reads {{image_url}}; `image` is kept
          // as an alias so a hand-edited template using either name still
          // renders a thumbnail instead of a broken-image icon.
          image_url: absoluteImage(item.image),
          image: absoluteImage(item.image),
        })),

        // {{cost.*}} block.
        cost: {
          // Named `subtotal` rather than derived in the template — EmailJS has
          // no arithmetic, so a receipt whose lines don't add up can only be
          // fixed here.
          subtotal:
            order.total - (order.deliveryCharge || 0) - (order.codCharge || 0),
          shipping: order.deliveryCharge,
          cod: order.codCharge || 0,
          total: order.total,
        },

        // Everything below exists so the receipt can answer the questions a
        // customer actually emails about: where is it going, when does it come,
        // and does the arithmetic add up.
        order_date: new Date().toLocaleDateString("en-IN", {
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
        delivery_address: `${order.address}, ${order.pincode}`,
        customer_phone: order.phone,
        delivery_estimate: "3-5 business days",
        payment_method: order.paymentMethod || "—",

        payment_note: paymentNote,
        unboxing_note:
          "Please film your unboxing. Claims for damage, missing items or a wrong product are assessed from that video and must reach us within 48 hours of delivery.",

        // Kept so a plain-text template still renders something useful.
        order_summary: buildSummary(order),
        total: `₹${order.total}`,
      },
      PUBLIC_KEY
    );
    return true;
  } catch (err) {
    console.error("Customer receipt email failed", err);
    return false;
  }
}
